import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../insurance/seguros-design.css';
import { url } from '../../../../../../env';
import { usePlateStep, usePlatesTimeline } from './hooks';
import { mapPlateStepStatus, reasonLabel } from './platesConfig';
import {
  ContextHeader, InfoRow,
  fmtDate, fmtDateTime, backendEventToTimelineItem,
} from './designHelpers';
import ModalShell from '../insurance/ModalShell';
import ComposeEmailModal from './ComposeEmailModal';
import UploadResponseModal from './UploadResponseModal';
import AdminActionsModal from './AdminActionsModal';
import HistoryDrawer from './HistoryDrawer';
import CorrectionForm from './CorrectionForm';

export default function PlatesStepModal({
  visible, onHide,
  quoteProcessId, personaId, persona,
  toast, onChanged,
}) {
  const [resolvedQpId, setResolvedQpId] = useState(quoteProcessId ?? null);

  useEffect(() => {
    if (quoteProcessId) { setResolvedQpId(quoteProcessId); return; }
    if (!visible || !personaId) return;
    const token = localStorage.getItem('token');
    axios.get(`${url}/quote-process/status/${personaId}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then((res) => {
        const pid = res?.data?.data?.process_id;
        if (pid) setResolvedQpId(pid);
      })
      .catch(() => {});
  }, [visible, quoteProcessId, personaId]);

  const effectiveQpId = quoteProcessId ?? resolvedQpId;
  const { step, plate, insuranceCompleted, loading, refresh } = usePlateStep(visible ? effectiveQpId : null);
  const stepStatus = useMemo(() => mapPlateStepStatus(step, plate), [step, plate]);
  const blocked = insuranceCompleted === false;

  useEffect(() => {
    if (visible) {
      console.log('[PlatesStepModal] persona:', persona);
      console.log('[PlatesStepModal] persona.infoBudget:', persona?.infoBudget);
      console.log('[PlatesStepModal] vehicle:', persona?.infoBudget?.vehicle);
      console.log('[PlatesStepModal] state_plate_id:', persona?.infoBudget?.state_plate_id);
      console.log('[PlatesStepModal] insuranceCompleted:', insuranceCompleted);
      console.log('[PlatesStepModal] effectiveQpId:', effectiveQpId);
    }
  }, [visible, persona, insuranceCompleted, effectiveQpId]);

  const view = useMemo(
    () => buildView({ persona, step, plate }),
    [persona, step, plate]
  );

  const lastRejection = useMemo(() => {
    const meta = step?.metadata || {};
    if (!meta.last_rejection_reason) return null;
    return {
      motivo: reasonLabel(meta.last_rejection_reason),
      fecha: fmtDateTime(meta.last_rejection_at),
      gestor: meta.last_rejection_provider_name || meta.last_email_provider_name || view.gestor.nombre,
    };
  }, [step, view.gestor.nombre]);

  const [tab, setTab] = useState('info');
  const [activeSub, setActiveSub] = useState(null);
  const [adminAction, setAdminAction] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleAction = (action) => {
    if (blocked) return;
    if (action === 'compose' || action === 'resend') setActiveSub('compose');
    else if (action === 'upload') setActiveSub('upload');
    else if (action === 'openHistorico') setDrawerOpen(true);
    else if (['reject', 'modify', 'cancel', 'reopen'].includes(action)) {
      setAdminAction(action); setActiveSub('admin');
    }
  };

  const handleSubDone = async () => {
    setActiveSub(null); setAdminAction(null);
    await refresh();
    onChanged?.();
  };

  return (
    <>
      <ModalShell visible={visible} onClose={onHide}>
        <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>

        <div style={{ padding: '26px 32px 0' }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Paso 6 · Placas</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.012em', marginBottom: 4 }}>
            {loading ? 'Cargando…' :
              stepStatus === 'completed' ? 'Placas activas' :
              stepStatus === 'rejected'  ? 'Solicitud rechazada' :
              stepStatus === 'cancelled' ? 'Placas canceladas' :
              stepStatus === 'sent'      ? 'Esperando tarjeta de circulación' :
              'Solicitar placas al gestor'}
          </div>
          {view.numeroContrato && (
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginBottom: 18 }}>
              Contrato No. {view.numeroContrato}
            </div>
          )}

          <div style={{
            background: 'var(--pei-bg-soft)',
            border: '1px solid var(--pei-gray-5)',
            borderRadius: 14,
            padding: '12px 14px',
            marginBottom: 18,
          }}>
            <ContextHeader cliente={view.cliente} vehiculo={view.vehiculo} contrato={view.contrato} compact />
          </div>

          <div className="tab-bar">
            <button className={`tab ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>
              <i className="pi pi-info-circle" style={{ fontSize: 13 }} /> Información
            </button>
            <button className={`tab ${tab === 'historico' ? 'active' : ''}`} onClick={() => setTab('historico')}>
              <i className="pi pi-history" style={{ fontSize: 13 }} /> Histórico
              <span className="count">{view.historicoCount}</span>
            </button>
          </div>
        </div>

        <div className="scrollable" style={{ padding: '20px 32px 4px', flex: 1, minHeight: 0 }}>
          {tab === 'info' && (
            <>
              {blocked && (
                <div className="banner warning" style={{ marginBottom: 16 }}>
                  <i className="pi pi-lock" />
                  <div>
                    <div className="b-title">Completa el paso de Seguros antes de tramitar las placas</div>
                    <div className="b-sub">No puedes componer el correo ni subir la tarjeta de circulación hasta que el seguro esté activo.</div>
                  </div>
                </div>
              )}
              <BaseInfoTab
                view={view}
                stepStatus={stepStatus}
                plate={plate}
                step={step}
                lastRejection={lastRejection}
                blocked={blocked}
                onAction={handleAction}
              />
            </>
          )}
          {tab === 'historico' && (
            <BaseHistoricoTab plateId={plate?.id} onAction={handleAction} />
          )}
        </div>

        <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', alignItems: 'center', gap: 10, background: 'white' }}>
          <button className="pei-btn pei-btn-ghost" onClick={() => setDrawerOpen(true)} disabled={!plate?.id}>
            <i className="pi pi-history" style={{ fontSize: 13 }} /> Ver histórico completo
          </button>
          <div style={{ flex: 1 }} />
          <button className="pei-btn pei-btn-ring" onClick={onHide}>Cerrar</button>
        </div>
      </ModalShell>

      <ComposeEmailModal
        visible={activeSub === 'compose'}
        onHide={() => setActiveSub(null)}
        qpId={effectiveQpId}
        persona={persona}
        view={view}
        toast={toast}
        onSent={handleSubDone}
      />
      <UploadResponseModal
        visible={activeSub === 'upload'}
        onHide={() => setActiveSub(null)}
        qpId={effectiveQpId}
        view={view}
        toast={toast}
        onCompleted={handleSubDone}
      />
      <AdminActionsModal
        visible={activeSub === 'admin'}
        onHide={() => { setActiveSub(null); setAdminAction(null); }}
        action={adminAction}
        qpId={effectiveQpId}
        plate={plate}
        view={view}
        toast={toast}
        onDone={handleSubDone}
        onRequestCorrection={() => { setAdminAction(null); setActiveSub('correct'); }}
      />
      <CorrectionForm
        visible={activeSub === 'correct'}
        onHide={() => setActiveSub(null)}
        plate={plate}
        toast={toast}
        onDone={handleSubDone}
      />
      <HistoryDrawer
        visible={drawerOpen}
        onHide={() => setDrawerOpen(false)}
        plateId={plate?.id}
        view={view}
      />
    </>
  );
}

function buildView({ persona, step, plate }) {
  const veh = persona?.infoBudget?.vehicle || {};
  const numeroContrato = persona?.infoBudget?.budget_id ?? persona?.tenant?.numeroContrato ?? null;
  const estadoPlaca = persona?.infoBudget?.state_plate_id ?? null;
  // Si hay placa VIGENTE, su gestor manda. Pero si la placa actual está cancelada
  // (intento previo) y se reenvió el correo, gana el gestor del ÚLTIMO envío
  // (metadata) — así no se queda con el nombre del intento anterior.
  const activePlate = plate && plate.status !== 'cancelled' ? plate : null;
  const gestorNombre =
    activePlate?.gestor_name ||
    step?.metadata?.last_email_provider_name ||
    plate?.gestor_name ||
    'Gestor de placas';
  return {
    cliente: persona?.name_syntage || persona?.name || '—',
    vehiculo: [veh.brand || veh.marca, veh.model || veh.modelo, veh.year || veh.anio].filter(Boolean).join(' '),
    vin: plate?.vin_card || '—',
    numeroContrato,
    contrato: numeroContrato ? `No. ${numeroContrato}` : null,
    estadoPlaca,
    gestor: {
      nombre: gestorNombre,
      correo: activePlate?.gestor_email || step?.metadata?.last_email_to || plate?.gestor_email || '',
    },
    extraccion: plate ? {
      placa: plate.plate_number,
      estado: plate.plate_state,
      folio: plate.folio,
      fechaInicio: plate.issued_at,
      fechaFin: plate.expires_at,
    } : null,
    historicoCount: (plate?.events?.length ?? 0),
    cc: step?.metadata?.last_email_cc || [],
    lastEmailAt: step?.metadata?.last_email_at,
  };
}

function GestorLogo({ size = 28 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 6,
      background: 'var(--pei-blue-deeper, #204b7b)', color: 'white',
      fontSize: size * 0.5,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <i className="pi pi-id-card" />
    </div>
  );
}

function BaseInfoTab({ view, stepStatus, plate, step, lastRejection, blocked, onAction }) {
  const gestor = view.gestor;

  const StatusBadgeRow = () => {
    if (stepStatus === 'pending') return (
      <div className="status-badge gray" style={{ padding: '10px 16px', fontSize: 14 }}>
        <span className="dot" /> Sin iniciar
      </div>
    );
    if (stepStatus === 'sent') return (
      <div className="status-badge blue" style={{ padding: '10px 16px', fontSize: 14 }}>
        <span className="dot" /> Enviado a {gestor.nombre} · esperando tarjeta de circulación
      </div>
    );
    if (stepStatus === 'completed') return (
      <div className="status-badge green" style={{ padding: '10px 16px', fontSize: 14 }}>
        <i className="pi pi-check" style={{ fontSize: 12 }} /> Completado · placas activas
      </div>
    );
    if (stepStatus === 'rejected') return (
      <div className="status-badge orange" style={{ padding: '10px 16px', fontSize: 14 }}>
        <i className="pi pi-exclamation-circle" style={{ fontSize: 12 }} /> Solicitud rechazada
      </div>
    );
    if (stepStatus === 'cancelled') return (
      <div className="status-badge red" style={{ padding: '10px 16px', fontSize: 14 }}>
        <i className="pi pi-times-circle" style={{ fontSize: 12 }} /> Placas canceladas
      </div>
    );
    return null;
  };

  return (
    <>
      {stepStatus === 'pending' && lastRejection && (
        <div className="banner warning" style={{ marginBottom: 16 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">Intento previo: {lastRejection.gestor} rechazó el {lastRejection.fecha}</div>
            <div className="b-sub">Motivo: {lastRejection.motivo}. Puedes intentar con otro gestor.</div>
          </div>
        </div>
      )}

      {stepStatus === 'cancelled' && (
        <div className="banner warning" style={{ marginBottom: 16 }}>
          <i className="pi pi-info-circle" />
          <div>
            <div className="b-title">Las placas anteriores fueron canceladas</div>
            <div className="b-sub">Inicia una nueva solicitud cuando estés listo.</div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Estado
        </div>
        <StatusBadgeRow />
      </div>

      {stepStatus === 'pending'   && <PendingActions onAction={onAction} gestor={gestor} blocked={blocked} />}
      {stepStatus === 'sent'      && <SentActions onAction={onAction} gestor={gestor} view={view} step={step} blocked={blocked} />}
      {stepStatus === 'completed' && <CompletedActions onAction={onAction} plate={plate} gestor={gestor} />}
      {stepStatus === 'rejected'  && <RejectedActions onAction={onAction} gestor={gestor} blocked={blocked} />}
      {stepStatus === 'cancelled' && <CancelledActions onAction={onAction} blocked={blocked} />}

      <div style={{ marginTop: 18 }}>
        <div className="pei-label" style={{ marginBottom: 8 }}>Resumen</div>
        <div className="section-card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
            <InfoRow label="Gestor de placas" value={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <GestorLogo size={22} />
                <span style={{ fontWeight: 600 }}>{gestor.nombre}</span>
              </div>
            )} />
            <InfoRow label="Estado de placa" value={
              <span style={{ fontWeight: 600 }}>{view.estadoPlaca || '—'}</span>
            } />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <InfoRow label="Vehículo" value={<span style={{ fontWeight: 600 }}>{view.vehiculo || '—'}</span>} />
            <InfoRow label="VIN" value={<span style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 13, color: 'var(--pei-text-label)' }}>{view.vin}</span>} />
          </div>
        </div>
      </div>
    </>
  );
}

function PendingActions({ onAction, gestor, blocked }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="pei-label">Próxima acción</div>
      <div style={{
        background: 'white',
        border: '1.5px solid var(--pei-cyan-bright)',
        borderRadius: 16,
        padding: 18,
        display: 'flex', alignItems: 'center', gap: 14,
        opacity: blocked ? 0.6 : 1,
      }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(50,184,223,0.14)', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)', flexShrink: 0 }}>
          <i className="pi pi-envelope" style={{ fontSize: 18 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pei-text)' }}>Componer correo de solicitud</div>
          <div style={{ fontSize: 12.5, color: 'var(--pei-text-helper)', marginTop: 2 }}>
            Plantilla precargada{gestor.nombre ? ` para ${gestor.nombre}` : ''} con datos del cliente y del vehículo.
          </div>
        </div>
        <button className="pei-btn pei-btn-primary floor-shadow" onClick={() => onAction('compose')} disabled={blocked}>
          Componer <i className="pi pi-arrow-right" style={{ fontSize: 12 }} />
        </button>
      </div>
    </div>
  );
}

function SentActions({ onAction, gestor, view, step, blocked }) {
  return (
    <div>
      <div className="pei-label">Próxima acción</div>
      <div style={{
        background: 'white',
        border: '1.5px solid var(--pei-cyan-bright)',
        borderRadius: 16,
        padding: 18,
        marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 14,
        opacity: blocked ? 0.6 : 1,
      }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(50,184,223,0.14)', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)', flexShrink: 0 }}>
          <i className="pi pi-paperclip" style={{ fontSize: 18 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pei-text)' }}>Subir tarjeta de circulación recibida</div>
          <div style={{ fontSize: 12.5, color: 'var(--pei-text-helper)', marginTop: 2 }}>
            Sube los PDFs que envió {gestor.nombre} · IA extrae los datos automáticamente.
          </div>
        </div>
        <button className="pei-btn pei-btn-primary floor-shadow" onClick={() => onAction('upload')} disabled={blocked}>
          Subir <i className="pi pi-arrow-up" style={{ fontSize: 12 }} />
        </button>
      </div>

      <details style={{
        background: 'var(--pei-bg-soft)',
        border: '1px solid var(--pei-gray-5)',
        borderRadius: 14,
        padding: '12px 16px',
        marginBottom: 14,
      }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--pei-text)', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="pi pi-send" style={{ fontSize: 12, color: 'var(--pei-cyan)' }} />
          Detalles del envío
          <i className="pi pi-chevron-down" style={{ fontSize: 10, marginLeft: 'auto', color: 'var(--pei-text-helper)' }} />
        </summary>
        <div style={{ paddingTop: 12, fontSize: 13, color: 'var(--pei-text-label)', lineHeight: 1.8 }}>
          <div><strong style={{ fontWeight: 600 }}>Enviado a:</strong> <span style={{ color: 'var(--pei-text)' }}>{gestor.correo || '—'}</span></div>
          {view.lastEmailAt && <div><strong style={{ fontWeight: 600 }}>Fecha:</strong> <span style={{ color: 'var(--pei-text)' }}>{fmtDateTime(view.lastEmailAt)}</span></div>}
          {view.cc?.length > 0 && <div><strong style={{ fontWeight: 600 }}>CC:</strong> {view.cc.join(', ')}</div>}
          {step?.metadata?.last_email_subject && (
            <div><strong style={{ fontWeight: 600 }}>Asunto:</strong> <span style={{ color: 'var(--pei-text)' }}>{step.metadata.last_email_subject}</span></div>
          )}
        </div>
      </details>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="pei-btn pei-btn-ring" onClick={() => onAction('resend')} disabled={blocked} style={{ height: 38, fontSize: 13, padding: '0 16px' }}>
          <i className="pi pi-refresh" style={{ fontSize: 12 }} /> Reenviar
        </button>
        <button className="pei-btn pei-btn-outline-danger" onClick={() => onAction('reject')} disabled={blocked} style={{ height: 38, fontSize: 13, padding: '0 16px' }}>
          <i className="pi pi-times-circle" style={{ fontSize: 12 }} /> Marcar como rechazada
        </button>
      </div>
    </div>
  );
}

function CompletedActions({ onAction, plate, gestor }) {
  const docs = plate?.documents || [];
  return (
    <div>
      <div style={{
        background: 'white',
        border: '1.5px solid rgba(34,236,154,0.50)',
        borderRadius: 16,
        overflow: 'hidden',
        marginBottom: 14,
      }}>
        <div style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--pei-gray-5)',
          background: 'rgba(34,236,154,0.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <GestorLogo size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--pei-text)' }}>
              {gestor.nombre} · {plate?.plate_state || '—'}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--pei-text-helper)', fontFamily: 'var(--pei-font-mono)' }}>
              Placa #{plate?.internal_id ?? plate?.id}
            </div>
          </div>
          {(() => {
            const est = plate?.effective_status || plate?.status;
            const map = {
              active:             { cls: 'green',  txt: 'Activa' },
              pending_activation: { cls: 'blue',   txt: 'Próxima a iniciar' },
              expired:            { cls: 'orange', txt: 'Vencida' },
              suspended:          { cls: 'orange', txt: 'Suspendida' },
              renewed:            { cls: 'gray',   txt: 'Renovada' },
              cancelled:          { cls: 'red',    txt: 'Cancelada' },
            };
            const s = map[est] || map.active;
            return (
              <span className={`status-badge ${s.cls}`} style={{ padding: '4px 10px', fontSize: 11 }}>
                <span className="dot" /> {s.txt}
              </span>
            );
          })()}
        </div>
        <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <InfoRow label="Placa" value={<span style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 13, fontWeight: 600 }}>{plate?.plate_number || '—'}</span>} />
          <InfoRow label="Estado" value={<span style={{ fontWeight: 600, fontSize: 13 }}>{plate?.plate_state || '—'}</span>} />
          <InfoRow label="Folio" value={<span style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 13, fontWeight: 600 }}>{plate?.folio || '—'}</span>} />
          <InfoRow label="Vigencia" value={<span style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(plate?.issued_at)} → {fmtDate(plate?.expires_at)}</span>} />
          <InfoRow label="Próx. renovación" value={<span style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(plate?.next_renewal_date || plate?.expires_at)}</span>} />
          <InfoRow label="VIN" value={<span style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 13, color: 'var(--pei-text-label)' }}>{plate?.vin_card || '—'}</span>} />
          <InfoRow label="Propietario" value={<span style={{ fontWeight: 600, fontSize: 13 }}>{plate?.owner_name || '—'}</span>} />
        </div>
        {docs.length > 0 && (
          <details style={{ padding: '0 18px 14px' }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--pei-cyan)', listStyle: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <i className="pi pi-paperclip" style={{ fontSize: 11 }} /> Ver documentos ({docs.length})
            </summary>
            <div style={{ paddingTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {docs.map(d => (
                <span key={d.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '5px 12px',
                  background: 'var(--pei-bg-soft)',
                  border: '1px solid var(--pei-gray-5)',
                  borderRadius: 999,
                  fontSize: 12,
                  color: 'var(--pei-text)',
                  fontWeight: 500,
                }}>
                  <i className="pi pi-file-pdf" style={{ fontSize: 11, color: 'var(--pei-danger)' }} />
                  {d.file_name}
                  {d.label && <span style={{ color: 'var(--pei-text-helper)', fontSize: 10 }}>· {d.label}</span>}
                </span>
              ))}
            </div>
          </details>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        <button className="pei-btn pei-btn-ring" onClick={() => onAction('reopen')} style={{ height: 40, fontSize: 13, padding: '0 14px', justifyContent: 'flex-start' }}>
          <i className="pi pi-refresh" style={{ fontSize: 12 }} /> Reabrir paso
        </button>
        <button className="pei-btn pei-btn-ring" onClick={() => onAction('modify')} style={{ height: 40, fontSize: 13, padding: '0 14px', justifyContent: 'flex-start' }}>
          <i className="pi pi-pencil" style={{ fontSize: 12 }} /> Modificar
        </button>
        <button className="pei-btn pei-btn-outline-danger" onClick={() => onAction('cancel')} style={{ height: 40, fontSize: 13, padding: '0 14px', justifyContent: 'flex-start' }}>
          <i className="pi pi-ban" style={{ fontSize: 12 }} /> Cancelar placa
        </button>
        <button className="pei-btn pei-btn-ring" onClick={() => onAction('openHistorico')} style={{ height: 40, fontSize: 13, padding: '0 14px', justifyContent: 'flex-start' }}>
          <i className="pi pi-history" style={{ fontSize: 12 }} /> Histórico
        </button>
      </div>
    </div>
  );
}

function RejectedActions({ onAction, gestor, blocked }) {
  return (
    <div>
      <div className="banner warning" style={{ marginBottom: 16 }}>
        <i className="pi pi-exclamation-triangle" />
        <div>
          <div className="b-title">{gestor.nombre} rechazó la solicitud</div>
          <div className="b-sub">Puedes intentar con el mismo gestor o cambiar a otro.</div>
        </div>
      </div>
      <button className="pei-btn pei-btn-primary floor-shadow" onClick={() => onAction('compose')} disabled={blocked} style={{ width: '100%' }}>
        Nueva solicitud de placas <i className="pi pi-arrow-right" style={{ fontSize: 12 }} />
      </button>
    </div>
  );
}

function CancelledActions({ onAction, blocked }) {
  return (
    <button className="pei-btn pei-btn-primary floor-shadow" onClick={() => onAction('compose')} disabled={blocked} style={{ width: '100%' }}>
      Iniciar nueva solicitud <i className="pi pi-arrow-right" style={{ fontSize: 12 }} />
    </button>
  );
}

function BaseHistoricoTab({ plateId, onAction }) {
  const { events, loading } = usePlatesTimeline(plateId);
  const items = (events || []).map(backendEventToTimelineItem);

  if (loading) return <div style={{ padding: 20, color: 'var(--pei-text-helper)' }}>Cargando…</div>;

  if (items.length === 0) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: 'var(--pei-text-helper)',
        background: 'var(--pei-bg-soft)',
        borderRadius: 12,
        border: '1px dashed var(--pei-gray-5)',
      }}>
        <i className="pi pi-history" style={{ fontSize: 24, color: 'var(--pei-gray-2)', marginBottom: 10, display: 'block' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pei-text-label)' }}>Aún no hay eventos</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>Cuando envíes el correo de solicitud aparecerá aquí.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ position: 'relative', paddingLeft: 22 }}>
        <div style={{ position: 'absolute', left: 9, top: 8, bottom: 8, width: 1.5, background: 'var(--pei-gray-5)' }} />
        {items.slice(0, 6).map((ev, i) => (
          <TimelineEvent key={i} event={ev} first={i === 0} />
        ))}
      </div>
      <button className="pei-btn pei-btn-ghost" onClick={() => onAction('openHistorico')} style={{ marginTop: 8, fontSize: 13 }}>
        Abrir histórico completo →
      </button>
    </div>
  );
}

function TimelineEvent({ event }) {
  const colorMap = {
    cyan:   { bg: 'rgba(50,184,223,0.16)',  color: 'var(--pei-cyan)' },
    green:  { bg: 'rgba(34,236,154,0.22)',  color: 'var(--pei-green-true)' },
    orange: { bg: 'rgba(255,108,14,0.16)',  color: 'var(--pei-orange)' },
    red:    { bg: 'rgba(220,38,38,0.14)',   color: 'var(--pei-danger)' },
    blue:   { bg: 'rgba(32,75,123,0.12)',   color: 'var(--pei-blue-deeper)' },
  };
  const c = colorMap[event.color] || colorMap.cyan;
  return (
    <div style={{ position: 'relative', marginBottom: 16, marginLeft: -22, paddingLeft: 32 }}>
      <div style={{
        position: 'absolute', left: 0, top: 2,
        width: 20, height: 20, borderRadius: 10,
        background: c.bg, color: c.color,
        display: 'grid', placeItems: 'center',
        border: '2px solid white',
      }}>
        <i className={`pi ${event.icon}`} style={{ fontSize: 10 }} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pei-text)', lineHeight: 1.35 }}>{event.title}</div>
      {event.sub && <div style={{ fontSize: 12.5, color: 'var(--pei-text-helper)', marginTop: 2 }}>{event.sub}</div>}
      <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{event.time}</span>
        {event.actor && <><span>·</span><span style={{ fontFamily: 'var(--pei-font-mono)' }}>{event.actor}</span></>}
      </div>
    </div>
  );
}
